import React, { useState, useEffect } from 'react';
import { Edit, Save, X, User, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ClaseConfig {
  id: string;
  dia_semana: number;
  hora: string;
  cancha: string;
  nivel: string;
  instructor?: string;
  activa: boolean;
}

interface Reserva {
  id: string;
  nombre: string;
  fecha: string;
  hora: string;
  cancha: string;
  nivel: string;
  instructor?: string;
}

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const TIME_SLOTS = ['07:00:00', '08:00:00', '09:00:00', '16:00:00', '17:00:00'];

const NIVEL_COLORS = {
  'P': '#10B981', // Verde - Principiante
  'I': '#F59E0B', // Amarillo - Intermedio
  'A': '#EF4444'  // Rojo - Avanzado
};

const NIVEL_OPTIONS = [
  { value: 'P', label: 'Principiante', color: '#10B981' },
  { value: 'I', label: 'Intermedio', color: '#F59E0B' },
  { value: 'A', label: 'Avanzado', color: '#EF4444' }
];

const INSTRUCTORS = [
  'Instructor A',
  'Instructor B', 
  'Instructor C',
  'Sin asignar'
];

export default function AdminCalendar() {
  const [clasesConfig, setClasesConfig] = useState<ClaseConfig[]>([]);
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [currentWeek, setCurrentWeek] = useState<Date[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState<ClaseConfig | null>(null);

  useEffect(() => {
    // Configurar la semana actual comenzando desde el martes 15 de julio 2025
    const today = new Date(2025, 6, 15); // 15 de julio 2025
    const weekDays = getWeekDays(today);
    setCurrentWeek(weekDays);
    
    fetchData();
    
    // Suscripción en tiempo real
    const configSubscription = supabase
      .channel('admin_config_channel')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'clases_config' },
        () => fetchClasesConfig()
      )
      .subscribe();

    const reservasSubscription = supabase
      .channel('admin_reservas_channel')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'reservas' },
        () => fetchReservas()
      )
      .subscribe();

    return () => {
      configSubscription.unsubscribe();
      reservasSubscription.unsubscribe();
    };
  }, []);

  const getWeekDays = (startDate: Date) => {
    const days = [];
    const monday = new Date(startDate);
    const dayOfWeek = startDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    monday.setDate(startDate.getDate() + diff);
    
    for (let i = 0; i < 5; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const fetchData = async () => {
    await Promise.all([fetchClasesConfig(), fetchReservas()]);
    setLoading(false);
  };

  const fetchClasesConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('clases_config')
        .select('*')
        .order('dia_semana')
        .order('hora')
        .order('cancha');

      if (error) throw error;
      setClasesConfig(data || []);
    } catch (error) {
      console.error('Error fetching clases config:', error);
    }
  };

  const fetchReservas = async () => {
    try {
      const startDate = formatDate(currentWeek[0]);
      const endDate = formatDate(currentWeek[4]);

      const { data, error } = await supabase
        .from('reservas')
        .select('*')
        .gte('fecha', startDate)
        .lte('fecha', endDate)
        .order('timestamp_creacion');

      if (error) throw error;
      setReservas(data || []);
    } catch (error) {
      console.error('Error fetching reservas:', error);
    }
  };

  const updateClaseConfig = async (config: ClaseConfig) => {
    try {
      const { error } = await supabase
        .from('clases_config')
        .update({
          nivel: config.nivel,
          instructor: config.instructor === 'Sin asignar' ? null : config.instructor,
          activa: config.activa
        })
        .eq('id', config.id);

      if (error) throw error;
      setEditingConfig(null);
    } catch (error: any) {
      alert('Error al actualizar configuración: ' + error.message);
    }
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const getConfigForSlot = (dayIndex: number, hora: string, cancha: string) => {
    return clasesConfig.find(c => 
      c.dia_semana === dayIndex + 1 && 
      c.hora === hora && 
      c.cancha === cancha
    );
  };

  const getReservasForSlot = (fecha: string, hora: string, cancha: string) => {
    return reservas.filter(r => 
      r.fecha === fecha && 
      r.hora === hora && 
      r.cancha === cancha
    );
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newStartDate = new Date(currentWeek[0]);
    newStartDate.setDate(newStartDate.getDate() + (direction === 'next' ? 7 : -7));
    const newWeek = getWeekDays(newStartDate);
    setCurrentWeek(newWeek);
    
    setTimeout(() => {
      fetchReservas();
    }, 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Panel de Administración - Calendario</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateWeek('prev')}
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
          >
            ← Semana Anterior
          </button>
          <span className="font-medium">
            {currentWeek[0]?.toLocaleDateString('es-ES', { 
              day: 'numeric', 
              month: 'long' 
            })} - {currentWeek[4]?.toLocaleDateString('es-ES', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </span>
          <button
            onClick={() => navigateWeek('next')}
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
          >
            Semana Siguiente →
          </button>
        </div>
      </div>

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
        <p className="text-blue-800 text-sm">
          <strong>Panel de Administrador:</strong> Aquí puedes cambiar el nivel de las clases, asignar instructores y ver los alumnos registrados en tiempo real.
        </p>
      </div>

      {/* Calendario de Administración */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Encabezados */}
          <div className="grid grid-cols-6 gap-2 mb-4">
            <div className="font-semibold text-center py-3 bg-gray-100 rounded">
              <Clock className="h-5 w-5 mx-auto mb-1" />
              Horario
            </div>
            {currentWeek.map((date, index) => (
              <div key={index} className="font-semibold text-center py-3 bg-blue-50 rounded">
                <div className="text-lg">{DAYS[index]}</div>
                <div className="text-sm text-gray-600">
                  {date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                </div>
              </div>
            ))}
          </div>

          {/* Filas de horarios */}
          {TIME_SLOTS.map((timeSlot) => (
            <div key={timeSlot} className="grid grid-cols-6 gap-2 mb-6">
              {/* Columna de horario */}
              <div className="flex items-center justify-center bg-gray-50 rounded p-4">
                <span className="text-lg font-bold">{formatTime(timeSlot)}</span>
              </div>

              {/* Columnas de días */}
              {currentWeek.map((date, dayIndex) => (
                <div key={dayIndex} className="space-y-3">
                  {/* Cancha 1 */}
                  {(() => {
                    const config1 = getConfigForSlot(dayIndex, timeSlot, 'C1');
                    const reservas1 = getReservasForSlot(formatDate(date), timeSlot, 'C1');

                    return (
                      <div className="border-2 rounded-lg p-3 bg-white min-h-[160px]">
                        {/* Header con configuración */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-bold">C1</span>
                          <span className="text-xs text-gray-500">
                            {reservas1.length}/4
                          </span>
                        </div>

                        {/* Configuración editable */}
                        {config1 && (
                          <div className="mb-3 p-2 bg-gray-50 rounded">
                            {editingConfig?.id === config1.id ? (
                              <div className="space-y-2">
                                <select
                                  value={editingConfig.nivel}
                                  onChange={(e) => setEditingConfig({
                                    ...editingConfig,
                                    nivel: e.target.value
                                  })}
                                  className="w-full text-xs border rounded px-2 py-1"
                                >
                                  {NIVEL_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                
                                <select
                                  value={editingConfig.instructor || 'Sin asignar'}
                                  onChange={(e) => setEditingConfig({
                                    ...editingConfig,
                                    instructor: e.target.value
                                  })}
                                  className="w-full text-xs border rounded px-2 py-1"
                                >
                                  {INSTRUCTORS.map(instructor => (
                                    <option key={instructor} value={instructor}>
                                      {instructor}
                                    </option>
                                  ))}
                                </select>

                                <div className="flex space-x-1">
                                  <button
                                    onClick={() => updateClaseConfig(editingConfig)}
                                    className="flex-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                  >
                                    <Save className="h-3 w-3 mx-auto" />
                                  </button>
                                  <button
                                    onClick={() => setEditingConfig(null)}
                                    className="flex-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                  >
                                    <X className="h-3 w-3 mx-auto" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center space-x-1">
                                    <div
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: NIVEL_COLORS[config1.nivel] }}
                                    ></div>
                                    <span className="text-xs font-medium">{config1.nivel}</span>
                                  </div>
                                  <button
                                    onClick={() => setEditingConfig(config1)}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    <Edit className="h-3 w-3" />
                                  </button>
                                </div>
                                <div className="text-xs text-gray-600">
                                  {config1.instructor || 'Sin instructor'}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Lista de alumnos */}
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            <User className="h-3 w-3 inline mr-1" />
                            Alumnos:
                          </div>
                          {reservas1.length > 0 ? (
                            reservas1.map((reserva) => (
                              <div
                                key={reserva.id}
                                className="text-xs bg-blue-50 rounded px-2 py-1"
                              >
                                {reserva.nombre}
                              </div>
                            ))
                          ) : (
                            <div className="text-xs text-gray-500 italic">
                              Sin alumnos registrados
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Cancha 2 */}
                  {(() => {
                    const config2 = getConfigForSlot(dayIndex, timeSlot, 'C2');
                    const reservas2 = getReservasForSlot(formatDate(date), timeSlot, 'C2');

                    return (
                      <div className="border-2 rounded-lg p-3 bg-white min-h-[160px]">
                        {/* Header con configuración */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-bold">C2</span>
                          <span className="text-xs text-gray-500">
                            {reservas2.length}/4
                          </span>
                        </div>

                        {/* Configuración editable */}
                        {config2 && (
                          <div className="mb-3 p-2 bg-gray-50 rounded">
                            {editingConfig?.id === config2.id ? (
                              <div className="space-y-2">
                                <select
                                  value={editingConfig.nivel}
                                  onChange={(e) => setEditingConfig({
                                    ...editingConfig,
                                    nivel: e.target.value
                                  })}
                                  className="w-full text-xs border rounded px-2 py-1"
                                >
                                  {NIVEL_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                
                                <select
                                  value={editingConfig.instructor || 'Sin asignar'}
                                  onChange={(e) => setEditingConfig({
                                    ...editingConfig,
                                    instructor: e.target.value
                                  })}
                                  className="w-full text-xs border rounded px-2 py-1"
                                >
                                  {INSTRUCTORS.map(instructor => (
                                    <option key={instructor} value={instructor}>
                                      {instructor}
                                    </option>
                                  ))}
                                </select>

                                <div className="flex space-x-1">
                                  <button
                                    onClick={() => updateClaseConfig(editingConfig)}
                                    className="flex-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                  >
                                    <Save className="h-3 w-3 mx-auto" />
                                  </button>
                                  <button
                                    onClick={() => setEditingConfig(null)}
                                    className="flex-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                                  >
                                    <X className="h-3 w-3 mx-auto" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center space-x-1">
                                    <div
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: NIVEL_COLORS[config2.nivel] }}
                                    ></div>
                                    <span className="text-xs font-medium">{config2.nivel}</span>
                                  </div>
                                  <button
                                    onClick={() => setEditingConfig(config2)}
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    <Edit className="h-3 w-3" />
                                  </button>
                                </div>
                                <div className="text-xs text-gray-600">
                                  {config2.instructor || 'Sin instructor'}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Lista de alumnos */}
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            <User className="h-3 w-3 inline mr-1" />
                            Alumnos:
                          </div>
                          {reservas2.length > 0 ? (
                            reservas2.map((reserva) => (
                              <div
                                key={reserva.id}
                                className="text-xs bg-green-50 rounded px-2 py-1"
                              >
                                {reserva.nombre}
                              </div>
                            ))
                          ) : (
                            <div className="text-xs text-gray-500 italic">
                              Sin alumnos registrados
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}